import dayjs from 'dayjs'
import _ from 'lodash'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Gender } from '@/core/types/shared'
import { CreatePatientDto, Parent, Patient } from '@/core/models/patient.model'
import { useCreatePatientMutation } from '@/core/services/patient'
import { useIsSA } from '@/hooks/useIsSA'
import { toUtcMidnight } from '@/utils/dateUtils'
import { getApiErrorMessage } from '@/utils/errors'
import { PatientProfileFormData } from './usePatientProfileForm'

const AGREEMENT_NOT_COLLECTED = 'not-collected'

/**
 * Creates a patient from the profile form. By default it opens the new patient's page;
 * `onCreated` replaces that navigation (e.g. the simplified reception flow keeps the user on its own screen).
 */
export function useCreatePatient(intro: boolean, onCreated?: (patientId: string, patient: Patient) => void) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isSA = useIsSA()

  const [commitMutation, { isLoading }] = useCreatePatientMutation()

  const createPatient = async (data: PatientProfileFormData) => {
    const createPatientDto = prepareCreatePatientDto(isSA && !data.agreement ? { ...data, agreement: AGREEMENT_NOT_COLLECTED } : data)
    try {
      const patient = await commitMutation(createPatientDto).unwrap()
      enqueueSnackbar(t('notification.patient.create.success'), { variant: 'success' })
      if (onCreated) onCreated(patient._id, patient)
      else navigate(`/patients/${patient._id}?intro=${intro}`, { replace: true })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.patient.create.error')), { variant: 'error' })
    }
  }

  return [createPatient, isLoading] as const
}

function prepareCreatePatientDto(data: PatientProfileFormData): CreatePatientDto {
  let createPatientDto: CreatePatientDto = {
    agreement: data.agreement,
    userInfo: {
      birthday: toUtcMidnight(data.birthday)?.toISOString(),
      birthPlaceCode: data.birthPlaceCode,
      gender: data.gender as Gender,
      name: data.name || '',
      birthName: data.birthName,
      birthFirstName: data.birthFirstName,
      surname: data.surname || '',
      birthSurname: data.birthSurname,
      identityJustificationType: data.identityJustificationType,
      identitySupplementaryAttribute: data.identitySupplementaryAttribute || undefined,
    },
  }
  createPatientDto = _.merge(
    {},
    createPatientDto,
    _.pickBy({
      identityConfirmed: data.identityConfirmed,
      insNIR: data.insNIR || undefined,
      insOID: data.insOID,
      insEmitter: data.insEmitter || undefined,
      contactInfo: _.pickBy({
        street: data.street,
        city: data.city,
        cp: data.cp ? data.cp : undefined,
        country: data.country,
        phone: data.phone,
      }),
      secu: data.secu,
      email: data.email,
      nationality: data.nationality,
      caisse: data.caisse,
      codeGestion: data.codeGestion,
      codeRegime: data.codeRegime,
      weight: data.weight !== undefined ? parseInt(data.weight) : undefined,
      height: data.height !== undefined ? parseInt(data.height) : undefined,
      allergy: data.allergy,
      vaccins: data.vaccins,
      notes: data.notes,
      background: data.background,
      treatment: data.treatment,
      parents: _.map(data.parents, (parent) => ({
        ...parent,
        cp: parent.cp ? parent.cp : undefined,
      })),
      insured: data.insured,
      insuredRelationship: data.insuredRelationship,
      insuranceProvider: data.insuranceProvider,
      primaryInsurance: data.primaryInsurance,
      secondaryInsurance: data.secondaryInsurance,
      primaryPolicy: data.primaryPolicy,
      secondaryPolicy: data.secondaryPolicy,
      primaryPharmacy: data.primaryPharmacy,
      pluginId: data.pluginId,
    })
  )

  if (data.privateDoctor && !_.isEmpty(data.privateDoctor)) {
    createPatientDto.attendingPhysician = {
      firstname: data.privateDoctor.firstname,
      lastname: data.privateDoctor.lastname,
      email: data.privateDoctor.email || undefined,
      phone: data.privateDoctor.phone || undefined,
      city: data.privateDoctor.city || undefined,
      cp: data.privateDoctor.cp ? data.privateDoctor.cp : undefined,
    }
  }

  if (data.parents && !_.isEmpty(data.parents)) {
    var parentsArr: Omit<Parent, '_id' | 'patient'>[] = []
    createPatientDto.parents?.forEach((parent) => {
      parentsArr.push({
        firstname: parent.firstname,
        lastname: parent.lastname,
        phone: parent.phone || undefined,
        city: parent.city || undefined,
        cp: parent.cp || undefined,
        email: parent.email || undefined,
      })
    })
    createPatientDto.parents = parentsArr
  }
  return createPatientDto
}
